import React, { useEffect, useState, useRef } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../../../core/common/loader";
import { Eye, EyeOff } from "lucide-react";
import {
  ROLE_SUPER_ADMIN,
  ROLE_SUPER_ADMIN_CHAR,
  ROLE_ADMIN,
  ROLE_ADMIN_CHAR,
} from "../../../environment";

type PasswordField = "password";

const Login = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const user = useSelector((state: any) => state.user.user);

  useEffect(() => {
    // This will run when the component mounts
    const checkalreadylogin = async () => {
      try {
        if (user) {
          if (
            user?.userDetails.role == ROLE_SUPER_ADMIN &&
            user?.userDetails.role_char.toLowerCase() ==
              ROLE_SUPER_ADMIN_CHAR.toLowerCase()
          ) {
            navigation(routes.superAdminDashboard);
          } else if (
            user?.userDetails.role == ROLE_ADMIN &&
            user?.userDetails.role_char.toLowerCase() ==
              ROLE_ADMIN_CHAR.toLowerCase()
          ) {
            navigation(routes.adminDashboard);
          } else {
            navigation(routes.adminDashboard);
          }
        }
      } catch (err) {}
    };

    checkalreadylogin();
  }, []);

  // State for email, password, and password visibility
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState(false);

  // ==== Forgot Password Flow ====
  const [showForgot, setShowForgot] = useState(false);
  const [emailFP, setEmailFP] = useState("");
  const [sending, setSending] = useState(false);

  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  // ==== Reset Password (post-OTP) ====
  const [showReset, setShowReset] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetVisibility, setResetVisibility] = useState({
    new: false,
    confirm: false,
  });

  const openForgot = () => {
    setEmailFP("");
    setShowForgot(true);
  };

  const closeForgot = () => setShowForgot(false);

  const sendOtp = async () => {
    if (!emailFP?.trim()) {
      showBasicAlert("Please enter email");
      return;
    }
    try {
      setSending(true);
      await api.post("userService/forgotPassword", { email: emailFP.trim() });
      setShowForgot(false);
      setOtp(["", "", "", ""]);
      setShowOtp(true);
      // focus first OTP box soon after open
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch (e: any) {
      showBasicAlert(e?.data?.message?.[0]?.msg || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const v = value.replace(/\D/g, "").slice(0, 1);
    const next = [...otp];
    next[index] = v;
    setOtp(next);
    if (v && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const arr = text.split("").slice(0, 4);
    const filled = [arr[0] || "", arr[1] || "", arr[2] || "", arr[3] || ""];
    setOtp(filled);
    setTimeout(() => otpRefs.current[Math.min(text.length, 3)]?.focus(), 0);
  };

  const submitOtp = async () => {
    const code = otp.join("");
    if (code.length !== 4) {
      showBasicAlert("Please enter the 4-digit OTP");
      return;
    }
    try {
      setSending(true);
      var result = await api.post("userService/verify_forgot_password", {
        email: emailFP.trim(),
        otp: code,
      });
      if (result.data.message[0].status) {
        setShowOtp(false);
        setNewPwd("");
        setConfirmPwd("");
        setShowReset(true);
      } else {
        showBasicAlert(result.data.message[0].msg);
      }
    } catch (e: any) {
      showBasicAlert(e?.data?.message?.[0]?.msg || "Invalid OTP");
    } finally {
      setSending(false);
    }
  };

  const submitNewPassword = async () => {
    if (!newPwd || !confirmPwd) {
      showBasicAlert("Please enter both fields");
      return;
    }
    if (newPwd.length < 8) {
      showBasicAlert("Password must be at least 8 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      showBasicAlert("New Password and Confirm Password must match");
      return;
    }

    try {
      setResetSending(true);

      const payload = {
        email: emailFP.trim(),
        password: newPwd,
        c_password: confirmPwd,
      };

      await api.post("userService/reset_forgot_password", payload);

      // Success UI
      showBasicAlert("Password reset successful. Please sign in.");
      setShowReset(false);
    } catch (e: any) {
      showBasicAlert(e?.data?.message?.[0]?.msg || "Failed to reset password");
    } finally {
      setResetSending(false);
    }
  };

  const navigationPath = (email: string) => {
    var data = {
      email: email,
    };
    navigation(routes.twoStepVerification2, { state: data });
  };

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prev) => !prev);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    try {
      event.preventDefault();
      const response = await api.post("userService/login", { email, password });
      if (response) {
        navigationPath(email); // Redirect to the dashboard
      }
    } catch (err: any) {
      showBasicAlert(err.data.message[0].msg);
    }
  };

  if (user) {
    return <Loader />;
  } else {
    return (
      <>
        <style>
          {`@charset "UTF-8";
            /*********************************************************************************/
            :root {
              --color-primary: #FF6A10;
              --color-secondary: #FF6A10B0;
              --radius: 6px;
              --transition: 0.3s;
              --f-bold:700;
              --font-primary: "Euclid Circular";
              --font-secondary: "Euclid Circular";
              --font-alt: "Source Serif Pro", serif;
              --h3: 34px;
            }
            .primary-color{color:#ff6a10;}
            .primary-color1{color:#1D71E3;}
            @font-face {
                font-family: 'Euclid Circular';
                src: url('../fonts/EuclidCircularA-Bold.woff2') format('woff2'),
                    url('../fonts/EuclidCircularA-Bold.woff') format('woff');
                font-weight: 700;
                font-style: normal;
                font-display: swap;
            }
            @font-face {
                font-family: 'Euclid Circular';
                src: url('../fonts/EuclidCircularA-Medium.woff2') format('woff2'),
                    url('../fonts/EuclidCircularA-Medium.woff') format('woff');
                font-weight: 500;
                font-style: normal;
                font-display: swap;
            }
            /**
             * Reset Styels
             */
            * {
              -webkit-box-sizing: border-box;
              -moz-box-sizing: border-box;
              box-sizing: border-box;
            }
            a {
              color: var(--color-heading);
              text-decoration: none;
              outline: none;
              transition: 0.3s;
            }
            a:hover,
            a:focus,
            a:active {
              text-decoration: none;
              outline: none;
              color: var(--color-primary);
            }
            a:focus {
              outline: none;
            }
            form {
              margin: 0;
            }
            /**
             * Typography
             */
            /*
              font-family: 'Source Serif Pro', serif; 
            */
            * {
              box-sizing: border-box;
            }
            html {
              margin: 0;
              padding: 0;
              font-size: 10px;
            }
            
            @media only screen and (max-width: 767px) {
              body {
                font-size: 16px;
                line-height: 1.5;
              }
            }
            h3,
            .h3 {
              font-size: var(--h3);
              line-height: 1.14;
              margin: 0;
              margin-bottom: 20px;
            }
            h3,
            .h3 {
              font-weight: var(--f-bold);
            }
            p {
              font-size: 18px;
              line-height: 1.45;
              margin-bottom: 30px;
            }
            .p-4 {
              padding: 1.5rem !important;
            }

            header {
                margin-top: 10px;
            }
            #loginForm button {
              background-color: #1D71E3;
              border: none;
              padding: 12px 0px;
              font-size: 18px !important;
              border-radius: 10px !important;
              color:#ffffff;
              font-family: inherit;
            }
            #main-wrapper {
              min-height: 82vh;
              display: flex;
              align-items: center; /* vertical center */
              justify-content: center; /* horizontal center */
            }
            .rbt-header-sticky {
             font-size: 18px;
              line-height: 1.5;
              font-family: var(--font-primary);
              background: url('assets/images/LoginBg.png') no-repeat center center fixed;
              background-size: cover;
              width: 100%;
              margin:0px;
              font-weight: 400;
              scroll-behavior: auto;
              position: relative;
              overflow-x: hidden;
              scrollbar-width: thin;
            }
            .hero-content {
              position: relative;
              z-index: 2;
            }
            .zyvo-login-register .form-group {
              position: relative;
              margin-bottom: 25px;
            }

            .zyvo-login-register .form-group label {
              position: absolute;
              top: -12px;
              left: 15px;
              background: #fff;
              padding: 0 6px;
              color: #0d6efd;
              font-weight: 400;
              font-size: 14px;
              display: flex;
              align-items: center;
              gap: 5px;
            }

            .zyvo-login-register .form-group label i {
              font-size: 15px;
            }
            .zyvo-login-register .form-group input {
              width: 100%;
              border: 1.5px solid #0d6efd;
              border-radius: 6px;
              padding: 12px 15px 12px 40px; /* space for icon */
              font-size: 14px;
              outline: none;
              transition: 0.3s ease;
            }

            .zyvo-login-register .form-group input:focus {
              border-color: #0047b3;
              box-shadow: 0 0 5px rgba(13, 110, 253, 0.3);
            }

            .zyvo-login-register .form-group label i {
              position: absolute;
              top: 27px;
                /* transform: translateY(-50%); */
              color: #555;
              font-size: 16px;
              left: 0px;
            }
            .zyvo-login-register p {
              font-size: 12px;
            }
            /* Centered box */
            .inner-box-login {
              display: flex;
              justify-content: center;
              align-items: center;
              background: #ffffff;
              border-radius: 12px;
              box-shadow: 0 6px 25px rgba(0, 0, 0, 0.1);
              padding: 30px 40px 20px 45px;
              width: 100%;
              max-width: 900px;
              margin: auto;
              overflow: hidden;
              column-gap: 50px;
            }
            .login-btn {
              display: grid;
              color: #1D71E3;
            }
            .text-end{
              text-align:right;
              margin-top:10px;
            }
            .login-description {
            
            }
            #loginForm input:-webkit-autofill,
            #loginForm input:-webkit-autofill:hover, 
            #loginForm input:-webkit-autofill:focus, 
            #loginForm input:-webkit-autofill:active {
                -webkit-box-shadow: 0 0 0 1000px #fff inset !important; /* change #fff to your bg color */
                -webkit-text-fill-color: #000 !important; /* text color */
                transition: background-color 5000s ease-in-out 0s;
            }

            /* Responsive tweaks */
            @media (min-width: 576px) {
              .container, .container-sm {
                max-width: 540px;
              }
            }
            @media (min-width: 768px) {
              .container {
                max-width: 720px;
              }
            }
            @media (min-width: 992px) {
              .container {
                max-width: 960px;
              }
            }
            @media (min-width: 1200px) {
              .container {
                max-width: 1140px;
              }
            }
            @media (min-width: 1400px) {
              .container {
                max-width: 1320px;
              }
            }
            .container{
              width: 100%;
              padding-right: var(--bs-gutter-x,.75rem);
              padding-left: var(--bs-gutter-x,.75rem);
              margin-right: auto;
              margin-left: auto;
            }
            @media only screen and (min-width: 1201px) and (max-width: 1400px) {
            h3,
              .h3 {
                font-size: 28px;
              line-height: 33px;
              }
            }
            @media only screen and (min-width: 991px) and (max-width: 1200px) {
              h3,
              .h3 {
                font-size: 24px;
              line-height: 30px;
              }
            }
            @media only screen and (min-width: 768px) and (max-width: 991px) {
              h3,
              .h3 {
                font-size: 28px;
              }
            }
            @media only screen and (max-width: 767px) {
              h3,
              .h3 {
                font-size: 24px;
              }
            }
            @media only screen and (min-width: 992px) and (max-width: 1199px) {
              p {
                font-size: 16px;
              }
            }
            @media only screen and (min-width: 768px) and (max-width: 991px) {
              p {
                font-size: 16px;
              }
            }
            @media (max-width: 991px) {
              .inner-box-login {
                width: 95%;
                flex-direction: column;
              }
              .image-hand img {
                max-width: 80%;
                height: auto;
              }
            }
            @media only screen and (max-width: 767px) {
            #main-wrapper {
              margin-top: 50px;
            }
            p {
                font-size: 16px;
            }
            .image-hand img {
                max-width: 100%;
                 height: auto;
            }
            }

          `}
        </style>
        <div className="rbt-header-sticky">
          <header className="rbt-header">
            <div className="header-sticky">
              <div className="container">
                <div className="mainbar-row rbt-navigation-center align-items-center">
                  <div className="header-left rbt-header-content">
                    <div className="header-info">
                      <div className="logo logo-dark">
                        <a href="https://www.zyvolabs.com">
                          <img
                            src="assets/images/logo.svg"
                            alt="Zyvo Logo"
                          ></img>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>
          <div id="main-wrapper" className="zyvo-login-register">
            <div className="hero-content">
              <div className="container">
                <div className="inner-box-login">
                  <div className="login-left">
                    <div className="image-hand">
                      <img src="assets/images/login.png" alt="Zyvo"></img>
                    </div>
                  </div>
                  <div className="login-right">
                    <h3 className="my-3">
                      <span className="primary-color1">Login</span> to account
                    </h3>
                    <p
                      className="login-description"
                      style={{ fontSize: "14px" }}
                    >
                      Access your tasks, notes, and projects anytime, anywhere -
                      and keep everything flowing in one place.
                    </p>
                    <form id="loginForm" onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label>
                          <i className="fas fa-envelope"></i> Email Id
                        </label>
                        <input
                          type="email"
                          id="email"
                          placeholder="codebest45@gmail.com"
                          onChange={(e) => setEmail(e.target.value)} // Update the email state on change
                          required
                        ></input>
                      </div>
                      <div className="form-group">
                        <label>
                          <i className="fas fa-lock"></i> Password
                        </label>
                        <input
                          type={passwordVisibility ? "text" : "password"}
                          id="password"
                          placeholder="**************"
                          required
                          onChange={(e) => setPassword(e.target.value)} // Update the password state on change
                        />
                        <span
                          onClick={() => togglePasswordVisibility("password")}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#555",
                          }}
                        >
                          {passwordVisibility ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </span>
                      </div>
                      <div className="login-btn">
                        <button className="btn rounded-0" type="submit">
                          Sign In
                        </button>
                      </div>
                    </form>
                    <p
                      className="forget-password text-end"
                      style={{ fontSize: "15px" }}
                    >
                      <a href="#">
                        <u style={{ fontSize: "15px" }}>Forget password</u>
                      </a>
                      ?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* ===== Forgot Password: Email Modal ===== */}
        {showForgot && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Forgot Password</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeForgot}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Enter your email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={emailFP}
                        onChange={(e) => setEmailFP(e.target.value)}
                        placeholder="you@example.com"
                        autoFocus
                        required
                      />
                    </div>
                    <p className="text-muted small mb-0">
                      We’ll send a 4-digit code to verify it’s you.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-light"
                      onClick={closeForgot}
                      disabled={sending}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={sendOtp}
                      disabled={sending}
                    >
                      {sending ? "Sending..." : "Next"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {/* ===== OTP Modal (4 digits) ===== */}
        {showOtp && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Enter OTP</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowOtp(false)}
                      disabled={sending}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p className="mb-3">
                      We sent a 4-digit code to <b>{emailFP}</b>.
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                      {[0, 1, 2, 3].map((i) => (
                        <input
                          key={i}
                          type="text"
                          inputMode="numeric"
                          className="form-control text-center"
                          style={{ width: 56, height: 56, fontSize: 22 }}
                          value={otp[i]}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          onPaste={handleOtpPaste}
                          ref={(el) => (otpRefs.current[i] = el)}
                          maxLength={1}
                        />
                      ))}
                    </div>
                    {/* optional: resend button */}
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={sendOtp}
                        disabled={sending}
                      >
                        {sending ? "Resending..." : "Resend code"}
                      </button>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-primary w-100"
                      onClick={submitOtp}
                      disabled={sending}
                    >
                      {sending ? "Verifying..." : "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {/* ===== Reset Password Modal (after OTP success) ===== */}
        {showReset && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Reset Password</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowReset(false)}
                      disabled={resetSending}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">New Password</label>
                      <div className="input-group">
                        <input
                          type={resetVisibility.new ? "text" : "password"}
                          className="form-control"
                          value={newPwd}
                          onChange={(e) => setNewPwd(e.target.value)}
                          placeholder="Enter new password"
                          autoFocus
                          required
                        />
                        <button
                          type="button"
                          className="input-group-text bg-white"
                          onClick={() =>
                            setResetVisibility((p) => ({ ...p, new: !p.new }))
                          }
                          disabled={resetSending}
                        >
                          <i
                            className={`ti ${
                              resetVisibility.new ? "ti-eye" : "ti-eye-off"
                            }`}
                          />
                        </button>
                      </div>
                      <small className="text-muted">
                        Minimum 8 characters.
                      </small>
                    </div>

                    <div className="mb-2">
                      <label className="form-label">Confirm Password</label>
                      <div className="input-group">
                        <input
                          type={resetVisibility.confirm ? "text" : "password"}
                          className="form-control"
                          value={confirmPwd}
                          onChange={(e) => setConfirmPwd(e.target.value)}
                          placeholder="Re-enter new password"
                          required
                        />
                        <button
                          type="button"
                          className="input-group-text bg-white"
                          onClick={() =>
                            setResetVisibility((p) => ({
                              ...p,
                              confirm: !p.confirm,
                            }))
                          }
                          disabled={resetSending}
                        >
                          <i
                            className={`ti ${
                              resetVisibility.confirm ? "ti-eye" : "ti-eye-off"
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {newPwd && confirmPwd && newPwd !== confirmPwd && (
                      <p className="text-danger small mb-0">
                        Passwords do not match.
                      </p>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-primary w-100"
                      onClick={submitNewPassword}
                      disabled={resetSending}
                    >
                      {resetSending ? "Saving..." : "Save New Password"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}
      </>
    );
  }
};

export default Login;
